'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.includes('#')) {
      e.preventDefault();
      const [path, hash] = href.split('#');
      
      if (pathname === path || path === '/homepage') {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        window.location.href = href;
      }
    }
  };

  const navLinks = [
    { id: 'nav_home', label: 'INICIO', href: '/homepage' },
    { id: 'nav_services', label: 'SERVICIOS', href: '/servicios' },
    { id: 'nav_shop', label: 'TIENDA', href: '/shop' },
    { id: 'nav_contact', label: 'CONTACTO', href: '/contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-black/80 backdrop-blur-xl border-b border-white/10' :'bg-transparent'
      }`}
    >
      <div className="max-w-[1800px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-36 lg:h-40">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-3 group">
            <div className="relative w-32 h-32 lg:w-32 lg:h-32">
              <AppImage
                src="/assets/images/Captura_de_pantalla_2026-02-03_a_la_s_11.55.27_p.m.-1770177906778.png"
                alt="LS Protection logo con escudo rojo"
                className="object-contain transition-transform duration-300 group-hover:scale-110"
                priority
              />
            </div>
            <div className="flex flex-col">
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks?.map((link) => (
              <Link
                key={link?.id}
                href={link?.href}
                onClick={(e) => handleNavClick(e, link?.href)}
                className="text-sm font-heading tracking-[0.2em] transition-all duration-300 relative group text-white/70 hover:text-white"
              >
                {link?.label}
                <span className="absolute bottom-[-4px] left-0 h-[2px] bg-[#E60000] transition-all duration-300 w-0 group-hover:w-full" />
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <a
            href="https://wa.me/18095848361?text=Hola,%20quiero%20agendar%20una%20cita"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:flex items-center gap-2 bg-[#E60000] hover:bg-[#E60000]/90 text-white px-8 py-3 rounded-none font-heading text-sm tracking-wider transition-all duration-300 hover:scale-105 uppercase btn-press"
          >
            AGENDAR CITA
            <Icon name="CalendarIcon" size={18} variant="outline" />
          </a>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={28}
              variant="outline"
            />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 py-6 transition-smooth">
            <nav className="flex flex-col gap-4">
              {navLinks?.map((link) => (
                <Link
                  key={link?.id}
                  href={link?.href}
                  onClick={(e) => {
                    handleNavClick(e, link?.href);
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-base font-heading tracking-[0.2em] py-3 px-4 transition-smooth text-white/70 hover:text-white hover:bg-white/5"
                >
                  {link?.label}
                </Link>
              ))}
              <a
                href="https://wa.me/18095848361?text=Hola,%20quiero%20agendar%20una%20cita"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 bg-[#E60000] hover:bg-[#E60000]/90 text-white px-8 py-4 rounded-none font-heading text-sm tracking-wider mt-4 uppercase btn-press"
              >
                AGENDAR CITA
                <Icon name="CalendarIcon" size={18} variant="outline" />
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}